import type { CSSProperties } from "react";
import type { BrandProfile } from "@/api/types";
import "./LabFormMockup.css";

type Props = {
  brand: BrandProfile;
  scale?: number;
};

/**
 * A4-portrait RDI-style return form preview. Rebuilt for Phase 3 to match the
 * supplied RDI reference form (`RDI - Example lab form 2.pdf`).
 *
 * Three tear-line-separated zones:
 *   1. Top form area — title, activation line, patient-detail boxes, kit-code
 *      panel + checklist.
 *   2. Sample-label triplet (left) + rotated Royal-Mail returns label (right).
 *   3. A second rotated returns label + a "SECURITY SEAL" banner.
 *
 * Tenant branding flows through the header logo / initials, the accent colour
 * and the activation URL only — the clinical / return content is fixed.
 *
 * Self-contained: own CSS in LabFormMockup.css. Barcodes / QR / datamatrix are
 * CSS-only (repeating-linear-gradients + a CSS checker pattern). No new deps.
 */
export function LabFormMockup({ brand, scale = 1 }: Props) {
  const accent = brand.accent || "#005EB8";
  const orgName = brand.orgName || brand.name || "Your Practice";
  const labAddress =
    brand.labAddress || "RDi Laboratories, 2 Queensway, Croydon, CR0 4BD";
  const activationUrl = brand.activationUrl || "real-digital.co.uk";

  const initials =
    brand.logoText ||
    orgName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 3)
      .toUpperCase();

  // Return address lines: tolerate commas or already-split lines.
  const addressLines = labAddress
    .split(/,|\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  const styleVars: CSSProperties = {
    ["--client-accent" as never]: accent,
    transform: `scale(${scale})`,
  };

  // A small reusable rotated Royal-Mail "Tracked 24" returns label.
  const ReturnsLabel = () => (
    <div className="lf-rm">
      <div className="lf-rm-inner">
        <div className="lf-rm-top">
          <span className="lf-rm-track">Tracked 24</span>
          <span className="lf-rm-q">Q24&nbsp;&nbsp;A2</span>
        </div>
        <div className="lf-barcode lf-barcode-128" />
        <div className="lf-rm-mid">
          <div className="lf-dm" aria-hidden />
          <div className="lf-rm-addr">
            {addressLines.map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
          </div>
        </div>
        <div className="lf-rm-fine">
          Internal Use — Scan the above left returns 2D barcode
        </div>
        <div className="lf-rm-fine">Postage on Account GB</div>
      </div>
    </div>
  );

  return (
    <div className="labform" style={styleVars}>
      {/* ───────────────────────── ZONE 1 — top form ───────────────────────── */}
      <div className="lf-zone lf-zone-1">
        <div className="lf-hdr">
          <div className="lf-title">
            <span className="b">RETURN</span> FORM
            <div className="lf-title-rule" />
          </div>
          <div className="lf-brand">
            {brand.logoSrc ? (
              <img className="lf-logo" src={brand.logoSrc} alt="" />
            ) : (
              <div className="lf-initials">{initials}</div>
            )}
          </div>
        </div>

        <p className="lf-activate">
          Activate your test kit using the kit code at{" "}
          <span className="lf-url">{activationUrl}</span> and return this form
          with your sample.
        </p>

        <div className="lf-cols">
          {/* Left column — patient detail boxes */}
          <div className="lf-col-l">
            {[
              "Test requested:",
              "Name:",
              "Surname:",
              "Sex:",
              "Date of Birth (DOB):",
            ].map((lab) => (
              <div className="lf-box-row" key={lab}>
                <span className="lf-box-lab">{lab}</span>
                <span className="lf-box-fill" />
              </div>
            ))}

            <div className="lf-subhead">
              Please complete the following information:
            </div>

            <div className="lf-dt-row">
              <span className="lf-box-lab">Date sample taken:</span>
              <span className="lf-box-fill lf-ph">
                <i>DD / MM / YYYY</i>
              </span>
            </div>
            <div className="lf-dt-row">
              <span className="lf-box-lab">Time sample taken:</span>
              <span className="lf-box-fill lf-ph">
                <i>:&nbsp;&nbsp;&nbsp;am/pm</i>
              </span>
            </div>

            <div className="lf-banner">
              PLEASE ADD THE DATE AND TIME YOU TOOK YOUR SAMPLE AND CAREFULLY
              PEEL THE LABEL AND ATTACH TO YOUR TUBE, ENSURING IT DOES NOT
              OVERLAP
            </div>
          </div>

          {/* Right column — kit code + checklist */}
          <div className="lf-col-r">
            <div className="lf-kitcode">Kit Code: 999999999</div>
            <div className="lf-checktitle">CHECKLIST</div>
            <ul className="lf-checklist">
              <li>
                <span className="lf-cbx">☑</span>
                <span>
                  Activate your kit on{" "}
                  <span className="lf-url">{activationUrl}</span>
                </span>
              </li>
              <li>
                <span className="lf-cbx">☑</span>
                <span>
                  Follow the instructions for use when performing your test
                </span>
              </li>
              <li>
                <span className="lf-cbx">☑</span>
                <span>Complete and return this form</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="lf-tear" aria-hidden />

      {/* ─────────────── ZONE 2 — sample labels + returns label ─────────────── */}
      <div className="lf-zone lf-zone-2">
        <div className="lf-labels-panel">
          {["LABEL 1", "LABEL 2 (if applicable)", "LABEL 3 (if applicable)"].map(
            (t) => (
              <div className="lf-label-card" key={t}>
                <div className="lf-label-fields">
                  <span className="lf-label-tab">{t}</span>
                  {[
                    "NAME",
                    "SURNAME",
                    "DOB",
                    "SEX",
                    "DATE/TIME",
                  ].map((f) => (
                    <div className="lf-label-row" key={f}>
                      <span className="lf-label-lab">{f}</span>
                      <span className="lf-label-ln" />
                    </div>
                  ))}
                </div>
                <div className="lf-qr" aria-hidden />
              </div>
            )
          )}
        </div>

        <div className="lf-rm-box">
          <ReturnsLabel />
        </div>
      </div>

      <div className="lf-tear" aria-hidden />

      {/* ─────────────── ZONE 3 — returns label + security seal ─────────────── */}
      <div className="lf-zone lf-zone-3">
        <div className="lf-rm-box lf-rm-box-wide">
          <ReturnsLabel />
        </div>
        <div className="lf-seal">
          <div className="lf-seal-banner">
            <span>SECURITY SEAL</span>
          </div>
          <div className="lf-seal-side">
            <div className="lf-qr lf-qr-sm" aria-hidden />
            <div className="lf-seal-ref">RDI/01/999999999/ZP6/0000001</div>
          </div>
        </div>
      </div>

      <div className="lf-watermark">Prepared via +CURA Studio · {orgName}</div>
    </div>
  );
}
